import {
  AGGREGATE_USER_FREQ_BY_GROUP,
  BEHAVIOR_AGGREGATION,
  BEHAVIOR_FREQUENCY,
  BEHAVIOR_MULTI_ATTRIBUTION,
  BEHAVIOR_MULTI_ATTRIBUTION_FILTER,
  FIRST_EVENT_TIME_PROPERTY,
  FUNNELS_TIME_TO_CONVERSION,
  PAGE_DURATION,
  RECURRING_REVENUE_CHANGE,
  RECURRING_REVENUE_CHANGE_TYPE,
} from 'common/query-builder/query-block/properties-menu/constants';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {segmentationToPropertyItem} from 'common/query-builder/query-block/properties-menu/util';
import {getSelectedLabelforBehavior} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {SPECIAL_EVENTS} from 'common/report/constants';
import {CurrentProject} from 'common/report/model-resources/projects';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {
  FeatureRolloutName,
  FeatureRollouts,
  isCompanyProfileEnabled,
  isMetricLevelDataGroupsEnabled,
} from 'common/report/util/feature-sampling';
import {Segmentation} from 'common/types/reports/query';
import {InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {getMetricDataGroupId} from 'common/query-builder/query-builder/util/data-groups';
import {DEFAULT_DATA_GROUP} from 'common/widgets/data-group-selector';

import {BehaviorShowClause, ShowClause} from '../models/show-clause';
import {hasOnlyEventBasedMetrics, hasOnlyFunnelMetrics} from '../models/utils';

export function getOtherFilterOptions({
  params,
  showClauses,
  hasGlobalDataGroup,
  insightsReportMode,
  multiAttributionProperty,
  currentProject,
}: {
  params: InsightsBookmarkParams;
  showClauses: ShowClause[];
  hasGlobalDataGroup: boolean;
  insightsReportMode: BookmarkType;
  multiAttributionProperty: Segmentation | null;
  currentProject: CurrentProject | null;
}): Array<PropertySectionItem<string>> {
  const hasEventsOnly = hasOnlyEventBasedMetrics(params);

  let showPQA = false;
  if (isCompanyProfileEnabled(currentProject)) {
    if (isMetricLevelDataGroupsEnabled(currentProject)) {
      showPQA = showClauses.some((clause) => clause instanceof BehaviorShowClause && getMetricDataGroupId(clause));
    } else {
      showPQA = hasGlobalDataGroup;
    }
  }

  return [
    /** displays a "First Seen" user property, which computes when the date the user was first tracked */
    FIRST_EVENT_TIME_PROPERTY,

    /** displays Recurring Revenue computed properties */
    ...(FeatureRollouts[FeatureRolloutName.Revenue].isEnabled(currentProject)
      ? [RECURRING_REVENUE_CHANGE, RECURRING_REVENUE_CHANGE_TYPE]
      : []),

    /** displays a filter on the selected multi-touch attribution property */
    ...(multiAttributionProperty && multiAttributionProperty.behavior
      ? [
          {
            ...segmentationToPropertyItem(multiAttributionProperty),
            ...BEHAVIOR_MULTI_ATTRIBUTION_FILTER,
            label: getSelectedLabelforBehavior(multiAttributionProperty.behavior),
          },
        ]
      : []),

    /** displays Frequency Per User and Aggregate Property Per User aggregations */
    ...(hasEventsOnly ? [BEHAVIOR_FREQUENCY, BEHAVIOR_AGGREGATION] : []),

    /** displays Funnels time to convert and a "Selected Step" event selection */
    ...(hasOnlyFunnelMetrics(params)
      ? [FUNNELS_TIME_TO_CONVERSION, {value: SPECIAL_EVENTS.SELECTED_STEP} as PropertySectionItem<string>]
      : []),

    /** displays a Duration property, for measuring time between 2 events */
    ...(hasEventsOnly && insightsReportMode === BookmarkType.Insights ? [PAGE_DURATION] : []),

    ...(showPQA ? [AGGREGATE_USER_FREQ_BY_GROUP] : []),
  ];
}

export function getOtherGroupByOptions({
  params,
  hasGlobalDataGroup,
  insightsReportMode,
  currentProject,
}: {
  params: InsightsBookmarkParams;
  hasGlobalDataGroup: boolean;
  insightsReportMode: BookmarkType;
  currentProject: CurrentProject;
}): Array<PropertySectionItem<string>> {
  const showClauses = params.sections.show;
  const hasEventsOnly = hasOnlyEventBasedMetrics(params);

  let showPQA = false;
  if (isCompanyProfileEnabled(currentProject)) {
    if (isMetricLevelDataGroupsEnabled(currentProject)) {
      showPQA =
        showClauses.some((clause) => clause instanceof BehaviorShowClause && getMetricDataGroupId(clause)) &&
        showClauses.every(
          (clause) =>
            (clause instanceof BehaviorShowClause && getMetricDataGroupId(clause) !== DEFAULT_DATA_GROUP.groupID) ||
            !(clause instanceof BehaviorShowClause),
        );
    } else {
      showPQA = hasGlobalDataGroup;
    }
  }

  return [
    /** displays a "First Seen" user property, which computes when the date the user was first tracked */
    FIRST_EVENT_TIME_PROPERTY,

    /** displays Recurring Revenue computed properties */
    ...(FeatureRollouts[FeatureRolloutName.Revenue].isEnabled(currentProject)
      ? [RECURRING_REVENUE_CHANGE, RECURRING_REVENUE_CHANGE_TYPE]
      : []),

    /** displays Frequency Per User and Aggregate Property Per User aggregations */
    ...(hasEventsOnly ? [BEHAVIOR_FREQUENCY, BEHAVIOR_AGGREGATION] : []),

    /** displays "Attributed By" for property attributions */
    ...(hasEventsOnly ? [BEHAVIOR_MULTI_ATTRIBUTION] : []),

    /** displays Funnels time to convert and a "Selected Step" event selection */
    /** @todo(metrics) SELECTED_STEP is an event selection but it is passed as a segment option, remove that abstraction */
    ...(hasOnlyFunnelMetrics(params)
      ? [FUNNELS_TIME_TO_CONVERSION, {value: SPECIAL_EVENTS.SELECTED_STEP} as PropertySectionItem<string>]
      : []),

    /** displays a Duration property, for measuring time between 2 events */
    ...(hasEventsOnly && insightsReportMode === BookmarkType.Insights ? [PAGE_DURATION] : []),

    /** displays "Users who did" for counting users within groups */
    ...(showPQA ? [AGGREGATE_USER_FREQ_BY_GROUP] : []),
  ];
}

export function getOtherAggregationOptions({
  hasEventsQuery,
  insightsReportMode,
}: {
  hasEventsQuery: boolean;
  insightsReportMode: BookmarkType;
}): Array<PropertySectionItem<string>> {
  const otherAggregationOptions: Array<PropertySectionItem<string>> = [
    ...(hasEventsQuery && insightsReportMode === BookmarkType.Insights ? [PAGE_DURATION] : []),
  ];
  return otherAggregationOptions;
}
